import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

// Schema for `GET /api/v1/auth/media-token`. Matches `MediaTokenResponse` in
// `hub/app/api/auth.py`. The token is one-per-agent (not one-per-message) — it
// authorizes any of the agent's media fetches over its short life. The
// `messageId` arg below is a *cache* key for React Query, so different media
// components don't accidentally share the same retry timer.
const mediaTokenResponseSchema = z.object({
  media_token: z.string().min(1),
  token_type: z.literal('bearer'),
  expires_in: z.number().int().positive(),
});

export type MediaToken = z.infer<typeof mediaTokenResponseSchema>;

/** Build the React Query key for a media token request. Exported so cache
 *  inspection / manual invalidation has a single source of truth. */
export const mediaTokenQueryKey = (messageId: string): readonly unknown[] =>
  ['auth', 'media-token', messageId] as const;

async function fetchMediaToken(): Promise<MediaToken> {
  const response = await apiClient.get('/auth/media-token');
  return mediaTokenResponseSchema.parse(response.data);
}

/**
 * Mint (or read from cache) a short-lived media token for media URLs.
 *
 * The Hub issues a 10-minute token; we keep the cache slightly under that
 * (9 minutes) so we never hand a media component an already-expired value.
 *
 *   - `staleTime: 9min` — fresh window: refetches won't fire.
 *   - `gcTime: 9min` — drop from cache after the same window; the next
 *     consumer will mint a new one rather than risk reusing a near-expiry
 *     value.
 *
 * Built but unused in M5-T2 — wired in M5-T5 when the media-rendering
 * components land. Per CTO guardrail #2 (`<img>` can't send Authorization),
 * the media URL gets `?media_token=<token>` appended at render time.
 */
export function useMediaToken(messageId: string): UseQueryResult<MediaToken, Error> {
  return useQuery<MediaToken, Error>({
    queryKey: mediaTokenQueryKey(messageId),
    queryFn: fetchMediaToken,
    staleTime: 9 * 60 * 1000,
    gcTime: 9 * 60 * 1000,
    retry: false,
  });
}
